import {
	type Message,
	type Modifier,
	TextDecorations,
	TextEffects,
	TextLinks,
	modifierObject,
	strings,
} from "@quickpoint.me/shared";
import parse, { Element, type HTMLReactParserOptions } from "html-react-parser";
import { useRecoilValue, useSetRecoilState } from "recoil";
import AnimatedWords from "../../displayComponents/pageElements/AnimatedWords";
import NDRToggle from "../../displayComponents/pageElements/NDRToggle";
import { PageIndex } from "../../state/page";
import { TapDirection } from "../../state/ui";
import { mdToTagMap } from "./map";
import replaceAnimatedText from "./replaceAnimatedText";
import replaceLinks from "./replaceLinks";
import replaceMd from "./replaceMd";

function hasDecoration(str: string): boolean {
	const decorationValues = Object.values(TextDecorations);
	return decorationValues.some((decoration) => str.includes(decoration));
}

function hasTextLinks(str: string): boolean {
	const decorationValues = Object.values(TextLinks);
	return decorationValues.some((decoration) => str.includes(decoration));
}

function hasTextEffects(str: string): boolean {
	const decorationValues = Object.values(TextEffects);
	return decorationValues.some((decoration) => str.includes(decoration));
}

function hasNDRToggle(str: string): boolean {
	return str.includes("NDRColorsToggle");
}

const linkbc = new BroadcastChannel(strings.broadcastChannel.viewer);

function replaceNDRToggle(text: string): string {
	return text.replace(/NDRColorsToggle/g, '<span id="ndr-toggle"></span>');
}

export default function MarkDownToTag({
	text,
	ownerName,
	modifier,
	hostId,
	tapped,
}: {
	text: string;
	ownerName?: string;
	modifier?: Modifier;
	hostId?: string;
	tapped: boolean;
}): string | JSX.Element | JSX.Element[] {
	const pageIndex = useRecoilValue(PageIndex);
	const setDirection = useSetRecoilState(TapDirection);

	const options: HTMLReactParserOptions = {
		replace(domNode) {
			if (domNode instanceof Element && domNode.attribs) {
				if (domNode.attribs.id === "ndr-toggle") {
					return (
						<span style={{ position: "relative", zIndex: 2 }}>
							<NDRToggle />
						</span>
					);
				}
				if (domNode.attribs.id === TextEffects.animate) {
					return (
						<AnimatedWords
							words={JSON.parse(domNode.attribs["data-words"])}
							tapped={tapped}
						/>
					);
				}

				if (domNode.name === "a") {
					const linkText =
						domNode.children?.[0] && domNode.children[0].type === "text"
							? domNode.children[0].data
							: "";
					const handleClick = (
						e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
					) => {
						e.stopPropagation();
						e.preventDefault();
						const type = domNode.attribs["data-type"];
						const link = domNode.attribs["data-link"];

						if (type === modifierObject["mailto:"].keyword) {
							window.location.href = `mailto:${domNode.attribs["data-link"]}`;
						}

						if (type === modifierObject["url:"].keyword) {
							let finalUrl = domNode.attribs["data-link"];
							// check if the data-link has http or https at the start if not add http
							if (!finalUrl.startsWith("http")) {
								finalUrl = `https://${finalUrl}`;
							}

							try {
								const url = new URL(finalUrl);
								if (url.hostname === "author.quickpoint.me") {
									const searchParamsString =
										sessionStorage.getItem("qpSearchParams");
									if (searchParamsString) {
										const searchParams = JSON.parse(searchParamsString);
										for (const key in searchParams) {
											if (
												Object.prototype.hasOwnProperty.call(searchParams, key)
											) {
												url.searchParams.set(key, searchParams[key]);
											}
										}
										finalUrl = url.toString();
									}
								}
							} catch (e) {
								console.error("Error processing URL:", e);
							}

							window.location.href = finalUrl;
						}

						if (type === modifierObject["backlink:"].keyword) {
							window.location.href = `/${ownerName}/${domNode.attribs["data-link"]}`;
						}

						if (type === modifierObject["toPage:"].keyword && hostId) {
							if (Number.parseInt(link) === pageIndex) return;
							const direction =
								Number.parseInt(link) > pageIndex ? "forward" : "back";
							setDirection(direction);
							const message: Message = {
								hostId,
								type: "QP",
								action: strings.messages.goToPage,
								payload: Number.parseInt(link),
							};
							linkbc.postMessage(message);
						}
					};

					return (
						// biome-ignore lint/a11y/useValidAnchor: <explanation>
						<a
							href="#"
							data-testid={domNode.attribs["data-testid"]}
							className="inline-link"
							onClick={handleClick}
							style={{
								background: "none",
								border: "none",
								padding: 0,
								cursor: "pointer",
								textDecoration: "underline",
								color: "inherit",
							}}
						>
							{linkText}
						</a>
					);
				}
			}

			return null;
		},
	};
	// check for the presence of any MDtags
	if (
		!hasDecoration(text) &&
		!hasTextLinks(text) &&
		!hasTextEffects(text) &&
		!hasNDRToggle(text)
	)
		return text;
	let textToParse: string = text;
	mdToTagMap.forEach((tags, md) => {
		const res = replaceMd(textToParse, md, tags);
		textToParse = res.parsed;
	});
	if (modifier) {
		textToParse = replaceLinks(textToParse, modifier);
	}
	textToParse = replaceAnimatedText(textToParse);
	textToParse = replaceNDRToggle(textToParse);
	const parsed = parse(textToParse, options);
	return parsed;
}
